import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';

import {
    Box, Icon, Grid, Flex
} from 'components/ui';
import Select from 'components/Inputs/Select/Select';
import ChangeMethodModal from 'components/GlobalModals/ChangeMethodModal/ChangeMethodModal';

import {
    colors, screenReaderOnlyStyle, forms, radii, borders, space
} from 'style/config';

import mediaUtils from 'utils/Media';
import UIUtils from 'utils/UI';
import skuUtils from 'utils/Sku';
import isFunction from 'utils/functions/isFunction';
import localeUtils from 'utils/LanguageLocale';
import { lineItem } from 'utils/LineItem';
import helpersUtils from 'utils/Helpers';
import agentAwareUtils from 'utils/AgentAware';
import { isChangeQuantityEnabled } from 'utils/arChangeQuantityFlag';
import CartLayoutEN from 'components/RwdBasket/Carts/CartLayout/locales/CartLayout_en_US';
import CartLayoutFR from 'components/RwdBasket/Carts/CartLayout/locales/CartLayout_fr_CA';
import { savedSkuIdsSetSelector, saveForLaterLoadingSelector } from 'selectors/saveForLater/saveForLaterSelectors';

const { Media } = mediaUtils;
const { getLocalisedDataFromResource } = localeUtils;
const { deferTaskExecution } = helpersUtils;

function SkuQty({
    sku, commerceId, qty, isOutOfStock, quantities, disabled = false, fetching, onChange, isReplenishment
}) {
    const id = 'qty_' + sku.skuId;
    const getText = getLocalisedDataFromResource(CartLayoutEN, CartLayoutFR);

    // Ensure the current qty is always selectable even when it exceeds maxPurchaseQuantity,
    // otherwise the native <select> falls back to the first option and the visual qty
    // silently drifts away from the Redux state. Coerce qty to a number so the comparison
    // is type-safe against any caller that dispatches qty as a string.
    const numericQty = Number(qty);
    const baseQuantities = quantities.includes(numericQty) ? quantities : [...quantities, numericQty];
    const options = isOutOfStock ? [...baseQuantities, lineItem.OOS_QTY] : baseQuantities;

    return (
        <>
            <label
                htmlFor={id}
                children={getText('qty')}
                css={screenReaderOnlyStyle}
            />
            <Select
                id={id}
                size={'xs'}
                value={qty}
                disabled={disabled || isOutOfStock}
                onChange={e => onChange({ commerceId, newQty: e.target.value, sku })}
                marginBottom={null}
                textIndent={'0px'}
                customStyle={{
                    input: {
                        width: '44px',
                        borderRadius: radii.full,
                        ...(!disabled && isReplenishment && isChangeQuantityEnabled() && {
                            borderColor: colors.midGray
                        }),
                        ...(disabled && {
                            borderColor: forms.DISABLED_BG,
                            borderOpacity: forms.DISABLED_OPACITY
                        }),
                        ...(fetching && {
                            pointerEvents: 'none'
                        })
                    }
                }}
                data-at={Sephora.debug.dataAt('sku_qty')}
            >
                {options.map((option, index) => (
                    <option
                        css={{ color: forms.COLOR }}
                        key={index.toString()}
                        value={option}
                    >
                        {option}
                    </option>
                ))}
            </Select>
        </>
    );
}

function SkuDelete({
    onChange, sku, productId, fetching, qty
}) {
    return (
        <Flex
            justifyContent={'center'}
            alignItems={'center'}
            width={'24px'}
            height={'24px'}
            borderRadius={radii.full}
            border={`1px solid ${colors.midGray}`}
            cursor={fetching ? 'initial' : 'pointer'}
            onClick={!fetching ? () => onChange({ sku, productId, qty }) : null}
            data-at={Sephora.debug.dataAt('bsk_sku_remove')}
        >
            <Box
                width={12}
                height={12}
            >
                <Icon
                    name={'trash'}
                    color={'gray'}
                    size={24}
                />
            </Box>
        </Flex>
    );
}

function SkuLoves({
    item, skuId, productId, loved, fetching, onChange
}) {
    const getText = getLocalisedDataFromResource(CartLayoutEN, CartLayoutFR);

    return (
        <Box
            paddingX={2}
            paddingY={'5px'}
            borderRadius={radii.full}
            width={!loved && ['98px', 'auto']}
            border={`1px solid ${loved ? colors.lightGray : colors.midGray}`}
            backgroundColor={loved ? colors.lightGray : undefined}
            textAlign='center'
            onClick={!loved && !fetching ? () => onChange({ item, skuId, productId }) : null}
            css={{ cursor: loved || fetching ? 'initial' : 'pointer', wordWrap: 'break-word' }}
            data-at={Sephora.debug.dataAt('bsk_sku_love')}
        >
            {getText(loved ? 'loved' : 'moveToLoves')}
        </Box>
    );
}

function SkuSaveForLater({
    item, skuId, productId, fetching, onChange, isSavedForLater = false, isSaveForLaterLoading = false
}) {
    const getText = getLocalisedDataFromResource(CartLayoutEN, CartLayoutFR);
    const disabled = fetching || isSavedForLater || skuUtils.isSample(item.sku);

    if (isSaveForLaterLoading) {
        return <Box css={[styles.saveForLaterSkeleton, UIUtils.SKELETON_ANIMATION]} />;
    }

    return (
        <Box
            {...styles.saveForLaterButton(disabled, isSavedForLater)}
            disabled={disabled}
            onClick={!disabled ? () => onChange({ item, skuId, productId }) : null}
        >
            {getText(isSavedForLater ? 'savedForLater' : 'saveForLater')}
        </Box>
    );
}

function ChangeMethod({
    label, onClick, fetching, onChangeMethod, isDisabled
}) {
    const disabled = fetching || !isFunction(onChangeMethod) || isDisabled;

    return (
        <Box
            minHeight={'24px'}
            paddingX={2}
            paddingY={'5px'}
            borderRadius={radii.full}
            color={disabled ? colors.gray : undefined}
            border={`1px solid ${disabled ? colors.lightGray : colors.midGray}`}
            backgroundColor={disabled ? colors.lightGray : undefined}
            textAlign='center'
            onClick={!disabled ? onClick : null}
        >
            {label}
        </Box>
    );
}

function ButtonRow(props) {
    const {
        item,
        isOutOfStock,
        onQtyUpdate,
        onDelete,
        onLoved,
        onSaveForLater,
        onChangeMethod,
        changeMethodLabel,
        breakpoint,
        itemDeliveryMethod,
        preferredStoreInfo,
        preferredZipCode,
        userId,
        hasMetFreeShippingThreshhold,
        isSignedInBIUser,
        isRewardFulfillmentVariant,
        isDisabled
    } = props;

    const showSaveForLater = localeUtils.isUS();
    const savedSkuIdsSet = useSelector(savedSkuIdsSetSelector);
    const isSaveForLaterLoading = useSelector(saveForLaterLoadingSelector);

    const [requestInTransit, setRequestInTransit] = useState(false);
    const [showChangeMethodModal, setShowChangeMethodModal] = useState(false);
    const [savedForLater, setSavedForLater] = useState(false);

    const { commerceId, qty, sku } = item;
    const isSavedForLater = savedSkuIdsSet.has(sku.skuId);
    const isAcceleratedAr = isChangeQuantityEnabled() && item.isReplenishment && !!sku.acceleratedPromotion;

    // Sync local state with Redux when item is removed from Save For Later via modal/carousel
    useEffect(() => {
        if (!isSavedForLater && savedForLater) {
            setSavedForLater(false);
        }
    }, [isSavedForLater, savedForLater]);

    const onChange = cb => arg => {
        setRequestInTransit(true);
        const result = cb(arg);

        if (result && typeof result.then === 'function') {
            return result.finally(() => setRequestInTransit(false));
        }

        setRequestInTransit(false);

        return result;
    };

    const openChangeMethodModal = () => deferTaskExecution(() => setShowChangeMethodModal(true));

    const onSaveForLaterClick = ({ skuId, productId }) => {
        if (savedForLater || isSavedForLater || requestInTransit) {
            return Promise.resolve();
        }

        setRequestInTransit(true);
        const result = onSaveForLater({ skuId, productId, item });

        if (result && typeof result.then === 'function') {
            return result
                .then(() => {
                    setSavedForLater(true);
                })
                .finally(() => setRequestInTransit(false));
        }

        setRequestInTransit(false);

        return Promise.resolve();
    };

    return (
        <>
            {showChangeMethodModal && (
                <ChangeMethodModal
                    isOpen={showChangeMethodModal}
                    closeChangeMethodModal={() => setShowChangeMethodModal(false)}
                    item={item}
                    onChangeMethod={onChangeMethod}
                    itemDeliveryMethod={itemDeliveryMethod}
                    preferredStoreInfo={preferredStoreInfo}
                    preferredZipCode={preferredZipCode}
                    userId={userId}
                    hasMetFreeShippingThreshhold={hasMetFreeShippingThreshhold}
                    isSignedInBIUser={isSignedInBIUser}
                    isRewardFulfillmentVariant={isRewardFulfillmentVariant}
                />
            )}
            <Media {...breakpoint}>
                <Flex
                    justifyContent={'space-between'}
                    marginTop={4}
                    minHeight={24}
                    fontSize={'sm'}
                >
                    <Grid
                        columns={'auto auto'}
                        gap={'6px'}
                        justifyItems='end'
                    >
                        <SkuQty
                            quantities={isAcceleratedAr ? [1] : skuUtils.purchasableQuantities(sku)}
                            sku={sku}
                            commerceId={commerceId}
                            isOutOfStock={isOutOfStock}
                            onChange={onChange(onQtyUpdate)}
                            qty={qty}
                            fetching={requestInTransit}
                            disabled={isOutOfStock || !isFunction(onQtyUpdate) || isAcceleratedAr}
                            isReplenishment={item.isReplenishment}
                        />
                        {(sku.type.toLowerCase() !== skuUtils.skuTypes.GWP.toLowerCase() ||
                            (Sephora.isAgent && agentAwareUtils.isAgentAwareSession())) && (
                            <SkuDelete
                                sku={sku}
                                qty={qty}
                                productId={sku.productId}
                                fetching={requestInTransit}
                                onChange={onChange(onDelete)}
                            />
                        )}
                    </Grid>

                    <Grid
                        columns={'auto auto'}
                        gap={['6px', '6px', '6px', '0px']}
                        paddingLeft={'6px'}
                    >
                        {!skuUtils.isBiReward(sku) &&
                            (showSaveForLater ? (
                                <SkuSaveForLater
                                    order={[1, 2]}
                                    skuId={sku.skuId}
                                    productId={sku.productId}
                                    item={item}
                                    fetching={requestInTransit}
                                    onChange={onSaveForLaterClick}
                                    isSavedForLater={savedForLater || isSavedForLater}
                                    isSaveForLaterLoading={isSaveForLaterLoading}
                                />
                            ) : (
                                isFunction(onLoved) && (
                                    <SkuLoves
                                        order={[1, 2]}
                                        loved={sku.actionFlags.myListStatus === 'added'}
                                        skuId={sku.skuId}
                                        item={item}
                                        productId={sku.productId}
                                        fetching={requestInTransit}
                                        onChange={onChange(onLoved)}
                                    />
                                )
                            ))}
                        <Media lessThan={'sm'}>
                            <ChangeMethod
                                label={changeMethodLabel}
                                fetching={requestInTransit}
                                onClick={openChangeMethodModal}
                                onChangeMethod={onChangeMethod}
                                isDisabled={isDisabled}
                            />
                        </Media>
                    </Grid>
                </Flex>
            </Media>
        </>
    );
}

const styles = {
    saveForLaterSkeleton: {
        height: `${space[5]}px`,
        width: `${space[9]}px`,
        borderRadius: radii.full
    },
    saveForLaterButton: (disabled, isSavedForLater) => ({
        paddingX: 2,
        paddingY: '5px',
        borderRadius: radii.full,
        width: !isSavedForLater && ['98px', 'auto'],
        color: disabled ? colors.gray : undefined,
        border: `${borders[1]} ${disabled ? colors.lightGray : colors.midGray}`,
        backgroundColor: disabled ? colors.lightGray : undefined,
        textAlign: 'center',
        css: { cursor: disabled ? 'initial' : 'pointer', wordWrap: 'break-word' }
    })
};

export default wrapFunctionalComponent(ButtonRow, 'ButtonRow');
